import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { toast } from '@shared/hooks/use-toast';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { FIELD_CLASSES, MODAL_PAGE_SIZE as PAGE_SIZE } from '@shared/utils/constants';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormService, FormField } from '@shared/services/FormServices';
import { PageHeader } from '@shared/components/PageHeader';
import { useTarrifList, useRateTypes, useUtilityServices, useRateStatus } from '../../../hooks';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { tarrifFilters, TariffListItem } from '../../../types';

interface SelectRecordsProps {
  storageKey?: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const SelectRecords: React.FC<SelectRecordsProps> = ({
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const dataLoadedRef = useRef(false);
  const [selectedRecords, setSelectedRecords] = useState<TariffListItem[]>([]);
  const selectedIds = useMemo(() => selectedRecords.map(r => String(r.id)), [selectedRecords]);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<any>({});

  const { data: rateTypesData } = useRateTypes();
  const { data: utilityServicesData } = useUtilityServices();
  const { data: rateStatusData } = useRateStatus();

  const filterFields: FormField[] = useMemo(() => {
    const rateTypeOptions = rateTypesData?.map((rt: any) => ({
      label: rt.value,
      value: rt.key.toString()
    })) || [];

    const utilityServiceOptions = utilityServicesData?.map((u: any) => ({
      label: u.name,
      value: u.name,
    })) || [];

    const rateStatusOptions = rateStatusData?.map((rs: any) => ({
      label: rs.value,
      value: rs.key.toString()
    })) || [];

    return [
      {
        name: "rateType",
        label: "Rate Type",
        type: "select",
        placeholder: "All Rate Types",
        options: rateTypeOptions,
        classes:FIELD_CLASSES
      },
      {
        name: "utilityService",
        label: "Utility Service",
        type: "select",
        placeholder: "All Utilities",
        options: utilityServiceOptions,
        classes:FIELD_CLASSES
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "All Status",
        options: rateStatusOptions,
        classes:FIELD_CLASSES
      }
    ];
  }, [rateTypesData, utilityServicesData, rateStatusData]);

  const filterForm = FormService.createForm({
    fields: filterFields,
    mode: "onSubmit",
  });

  const handleFilterSubmit = (data: any) => {
    setAppliedFilters(data);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const filters: tarrifFilters = useMemo(() => {
    const apiFilters: tarrifFilters = {
      page: currentPage,
      limit: PAGE_SIZE,
      remote_utility_id: Number(remoteUtilityId),
    };
    
    if (appliedFilters.rateType) {
      apiFilters.rateType = appliedFilters.rateType;
    }
    if (appliedFilters.utilityService) {
      apiFilters.utilityService = appliedFilters.utilityService;
    }
    if (appliedFilters.status) {
      apiFilters.status = [appliedFilters.status as any];
    }
    if (searchQuery) {
      apiFilters.search_data = searchQuery;
    }
    return apiFilters;
  }, [currentPage, remoteUtilityId, appliedFilters, searchQuery]);

  const { data } = useTarrifList(filters, remoteUtilityId as string);
  console.log(data, 'data');

  const tariffData: TariffListItem[] = data?.result || [];
  const totalItems = data?.count || 0;

  const saveStepData = useCallback(
    (dataToSave: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...dataToSave,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Restore saved data on mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData?.selectedRecords) {
          setSelectedRecords(savedStepData.selectedRecords);
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters.rateType, appliedFilters.utilityService, searchQuery]);

  const handleContinue = () => {
    if (clearValidationError) {
      clearValidationError();
    }

    if (selectedRecords.length === 0) {
      if (setValidationError) {
        setValidationError("Please select at least one tariff to continue.");
      }
      return;
    }

    saveStepData({
      selectedRecords,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    if (logTimeUntilStep) {
      logTimeUntilStep("Select Records");
    }

    if (onNext) onNext();
  };

  const handleSelectionChange = (newSelectedIds: (string | number)[]) => {
    const numericIds = newSelectedIds.map(Number);
    setSelectedRecords(prevSelected => {
      const prevMap = new Map(prevSelected.map(p => [p.id, p]));
      const currentDataMap = new Map(tariffData.map(d => [d.id, d]));
      
      const newRecords = numericIds.map(id => currentDataMap.get(id) || prevMap.get(id));
      return newRecords.filter((r): r is TariffListItem => r !== undefined);
    });
  };

  const handleSearch = (term: string) => {
    setSearchQuery(term);
  };

  const columns: TableColumn<TariffListItem>[] = useMemo(() => [
    {
      key: "rateName",
      header: "Tariff Name",
      width: "w-48",
      render: (_, row) => (
        <span className="text-sm font-medium text-gray-900">
          {row.rateName}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Type",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "rateTypeDisplay",
      header: "Tariff Type",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "currentVersionNumber",
      header: "Version",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "effectiveDate",
      header: "Valid From",
      width: "w-40",
    },
    {
      key: "statusDisplay",
      header: "Status",
      width: "w-32",
      type: "badge",
      render: (value: string) => {
        const label = value?.toLocaleLowerCase() === 'active';
        const className = label
          ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
          : "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";

        return (
          <span
            className={`text-xs font-medium px-2.5 py-0.5 rounded border ${className}`}
          >
            {value}
          </span>
        );
      },
    }
  ], []);

  return (
    <div className="space-y-6">
      <div className="rounded-xl">
        <PageHeader 
          title="Select Tariffs" 
          subtitle="Choose the tariffs that will receive the bulk change." 
        />

        <div className="rounded-lg p-2 mt-6">
          <GenericTable
            data={tariffData}
            columns={columns}
            filterPanel={
              <div className="w-full">
                <DynamicForm 
                  fields={filterFields}
                  form={filterForm}
                  gridColumns={3}
                  onSubmit={handleFilterSubmit}
                  onReset={handleFilterReset}
                  config={{
                    showSubmitButton: true,
                    submitButtonText: "Apply Filters",
                    showResetButton: true,
                    resetButtonText: "Clear Filters"
                  }}
                  />
              </div>
            }
            rowKey="id"
            selectable={true}
            selectedRows={selectedIds}
            onSelectionChange={handleSelectionChange}
            searchPlaceholder="Search tariffs by code, name or status..."
            searchParamName="search"
            onSearch={handleSearch}
            pagination={{
              currentPage,
              totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
              totalItems,
              startItem: (currentPage - 1) * PAGE_SIZE + 1,
              endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
              itemsPerPage: PAGE_SIZE,
            }}
            onPageChange={setCurrentPage}
          />
        </div>

        {selectedIds.length === 0 && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg text-orange-800 text-sm">
            Please select at least one tariff to continue.
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4">
        <Button
          onClick={handleContinue}
          disabled={isValidating}
          className="min-w-[120px]"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

